import React, { useState, useEffect, useCallback } from "react";
import {
  SafAnchor,
  SafDialog,
  SafButton,
  SafIcon,
  SafText,
  SafTextField,
} from "@saffron/core-components/react";
import { useParams } from "react-router-dom";

const SupportingPatternTable = (props) => {
  const [supportingPatternData, setSupportingPatternData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteItemType, setDeleteItemType] = useState("");
  const [itemToEdit, setItemToEdit] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [itemToUpdate, setItemToUpdate] = useState(null);
  const [editFormData, setEditFormData] = useState({
    displayName: "",
    hyperlink: "",
  });

  // Get the asset ID from URL params
  const { assetId: urlAssetId } = useParams();
  const assetId = urlAssetId || "";

  // Middleware endpoint helper function
  const getMiddlewareEndpoint = (operation) => {
    const baseEndpoint = process.env.REACT_APP_MIDDLEWARE_ENDPOINT;
    if (!baseEndpoint) {
      console.error("Middleware endpoint is not defined.");
      return "";
    }
    return `${baseEndpoint}&operation=${operation}`;
  };

  const getDiagramsData = useCallback(async () => {
    setIsLoading(true);
    const dbName = process.env.REACT_APP_DB_TARGET;
    const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
    const operation = "get";
    const endpoint = getMiddlewareEndpoint(operation);

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`,
          partitionKey: assetId,
        }),
      });

      if (!response.ok) {
        console.error(
          `Error fetching data: ${response.status} ${response.statusText}`
        );
        return;
      }

      const data = await response.json();

      // Handle both array and single object responses
      if (Array.isArray(data)) {
        setSupportingPatternData(data);
      } else if (data) {
        setSupportingPatternData([data]);
      } else {
        setSupportingPatternData([]);
      }
    } catch (err) {
      console.error("Error fetching supporting pattern data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    getDiagramsData();
  }, [getDiagramsData]);

  const handleEditClick = (item) => {
    setItemToUpdate(item);
    setEditFormData({
      displayName: item.displayName,
      hyperlink: item.hyperlink,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateItem = async () => {
    if (!itemToUpdate) return;

    const { type, idx } = itemToUpdate;
    let { displayName, hyperlink } = editFormData;

    if (hyperlink && !/^https?:\/\//i.test(hyperlink)) {
      hyperlink = `https://${hyperlink}`;
    }

    const dbName = process.env.REACT_APP_DB_TARGET;
    const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;

    try {
      // Get the latest document
      const getOperation = "get";
      const getEndpoint = getMiddlewareEndpoint(getOperation);
      const fetchResponse = await fetch(getEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`,
          partitionKey: assetId,
        }),
      });

      if (!fetchResponse.ok) {
        throw new Error(
          `Error fetching data: ${fetchResponse.status} ${fetchResponse.statusText}`
        );
      }

      const existingData = await fetchResponse.json();
      const dataToUpdate = Array.isArray(existingData)
        ? existingData[0]
        : existingData;

      if (!dataToUpdate) {
        throw new Error("No data found to update");
      }

      // Create a deep copy to avoid direct state mutation
      const updatedItem = JSON.parse(JSON.stringify(dataToUpdate));

      // Update the correct array based on type
      if (type === "diagram") {
        updatedItem.arch_diags[idx] = {
          diagram_name: displayName,
          hyperlink,
        };
      } else if (type === "decision") {
        updatedItem.adrs[idx] = { adr_name: displayName, hyperlink };
      } else if (type === "roadmap") {
        updatedItem.road_map[idx] = {
          road_map_name: displayName,
          hyperlink,
        };
      }

      // Send the update to the server
      const insertOperation = "insert";
      const insertEndpoint = getMiddlewareEndpoint(insertOperation);
      const updateResponse = await fetch(insertEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          jsonData: updatedItem,
          partitionKey: assetId,
        }),
      });

      if (!updateResponse.ok) {
        throw new Error(
          `Error updating data: ${updateResponse.status} ${updateResponse.statusText}`
        );
      }

      // Refetch data to ensure UI is in sync
      getDiagramsData();
      setIsEditDialogOpen(false);
      setItemToUpdate(null);
    } catch (err) {
      console.error(`Error updating ${type}:`, err);
    }
  };

  const handleDeleteDiagram = async () => {
    if (deleteIndex === null || itemToEdit === null) return;

    const dbName = process.env.REACT_APP_DB_TARGET;
    const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;

    try {
      // First get the latest data
      const getOperation = "get";
      const getEndpoint = getMiddlewareEndpoint(getOperation);
      const fetchResponse = await fetch(getEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`,
          partitionKey: assetId,
        }),
      });

      if (!fetchResponse.ok) {
        console.error(
          `Error fetching data: ${fetchResponse.status} ${fetchResponse.statusText}`
        );
        return;
      }

      const existingData = await fetchResponse.json();
      const dataToUpdate = Array.isArray(existingData)
        ? existingData[0]
        : existingData;

      if (!dataToUpdate) {
        console.error("No data found to update");
        return;
      }

      // Update the data
      const updatedDiagrams = dataToUpdate.arch_diags.filter(
        (_, index) => index !== deleteIndex
      );
      const updatedItem = { ...dataToUpdate, arch_diags: updatedDiagrams };

      // Send the update
      const insertOperation = "insert";
      const insertEndpoint = getMiddlewareEndpoint(insertOperation);
      const updateResponse = await fetch(insertEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          jsonData: updatedItem,
          partitionKey: assetId,
        }),
      });

      if (!updateResponse.ok) {
        console.error(
          `Error updating data: ${updateResponse.status} ${updateResponse.statusText}`
        );
        return;
      }

      // Update local state
      setSupportingPatternData((prevData) =>
        prevData.map((item) => (item.id === itemToEdit.id ? updatedItem : item))
      );
    } catch (err) {
      console.error("Error deleting diagram:", err);
    }

    setIsDeleteDialogOpen(false);
  };

  const handleDeleteDecision = async () => {
    if (deleteIndex === null || itemToEdit === null) return;

    const dbName = process.env.REACT_APP_DB_TARGET;
    const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;

    try {
      // First get the latest data
      const getOperation = "get";
      const getEndpoint = getMiddlewareEndpoint(getOperation);
      const fetchResponse = await fetch(getEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`,
          partitionKey: assetId,
        }),
      });

      if (!fetchResponse.ok) {
        console.error(
          `Error fetching data: ${fetchResponse.status} ${fetchResponse.statusText}`
        );
        return;
      }

      const existingData = await fetchResponse.json();
      const dataToUpdate = Array.isArray(existingData)
        ? existingData[0]
        : existingData;

      if (!dataToUpdate) {
        console.error("No data found to update");
        return;
      }

      // Update the data
      const updatedDecisions = dataToUpdate.adrs.filter(
        (_, index) => index !== deleteIndex
      );
      const updatedItem = { ...dataToUpdate, adrs: updatedDecisions };

      // Send the update
      const insertOperation = "insert";
      const insertEndpoint = getMiddlewareEndpoint(insertOperation);
      const updateResponse = await fetch(insertEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          jsonData: updatedItem,
          partitionKey: assetId,
        }),
      });

      if (!updateResponse.ok) {
        console.error(
          `Error updating data: ${updateResponse.status} ${updateResponse.statusText}`
        );
        return;
      }

      // Update local state
      setSupportingPatternData((prevData) =>
        prevData.map((item) => (item.id === itemToEdit.id ? updatedItem : item))
      );
    } catch (err) {
      console.error("Error deleting decision:", err);
    }

    setIsDeleteDialogOpen(false);
  };

  const handleDeleteRoadmap = async () => {
    if (deleteIndex === null || itemToEdit === null) return;

    const dbName = process.env.REACT_APP_DB_TARGET;
    const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;

    try {
      // First get the latest data
      const getOperation = "get";
      const getEndpoint = getMiddlewareEndpoint(getOperation);
      const fetchResponse = await fetch(getEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`,
          partitionKey: assetId,
        }),
      });

      if (!fetchResponse.ok) {
        console.error(
          `Error fetching data: ${fetchResponse.status} ${fetchResponse.statusText}`
        );
        return;
      }

      const existingData = await fetchResponse.json();
      const dataToUpdate = Array.isArray(existingData)
        ? existingData[0]
        : existingData;

      if (!dataToUpdate) {
        console.error("No data found to update");
        return;
      }

      // Update the data
      const updatedRoadmaps = dataToUpdate.road_map.filter(
        (_, index) => index !== deleteIndex
      );
      const updatedItem = { ...dataToUpdate, road_map: updatedRoadmaps };

      // Send the update
      const insertOperation = "insert";
      const insertEndpoint = getMiddlewareEndpoint(insertOperation);
      const updateResponse = await fetch(insertEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          jsonData: updatedItem,
          partitionKey: assetId,
        }),
      });

      if (!updateResponse.ok) {
        console.error(
          `Error updating data: ${updateResponse.status} ${updateResponse.statusText}`
        );
        return;
      }

      // Update local state
      setSupportingPatternData((prevData) =>
        prevData.map((item) => (item.id === itemToEdit.id ? updatedItem : item))
      );
    } catch (err) {
      console.error("Error deleting roadmap:", err);
    }

    setIsDeleteDialogOpen(false);
  };

  const triggerDelete = () => {
    if (deleteItemType === "diagram") {
      handleDeleteDiagram();
    } else if (deleteItemType === "decision") {
      handleDeleteDecision();
    } else if (deleteItemType === "roadmap") {
      handleDeleteRoadmap();
    }
  };

  // Function to get display items based on props.name
  const getDisplayItems = () => {
    const displayItems = [];

    if (supportingPatternData.length > 0) {
      supportingPatternData.forEach((item, itemIndex) => {
        // Only process arrays that exist
        if (props.name === "add diagram" && Array.isArray(item.arch_diags)) {
          item.arch_diags.forEach((diag, idx) => {
            if (diag.diagram_name && diag.hyperlink) {
              displayItems.push({
                displayName: diag.diagram_name,
                hyperlink: diag.hyperlink,
                type: "diagram",
                itemIndex,
                idx,
              });
            }
          });
        }

        if (props.name === "add decision" && Array.isArray(item.adrs)) {
          item.adrs.forEach((adr, idx) => {
            if (adr.adr_name && adr.hyperlink) {
              displayItems.push({
                displayName: adr.adr_name,
                hyperlink: adr.hyperlink,
                type: "decision",
                itemIndex,
                idx,
              });
            }
          });
        }

        if (props.name === "add roadmap" && Array.isArray(item.road_map)) {
          item.road_map.forEach((roadMap, idx) => {
            if (roadMap.road_map_name && roadMap.hyperlink) {
              displayItems.push({
                displayName: roadMap.road_map_name,
                hyperlink: roadMap.hyperlink,
                type: "roadmap",
                itemIndex,
                idx,
              });
            }
          });
        }
      });
    }

    return displayItems;
  };

  // Get the items to display
  const displayItems = getDisplayItems();

  // Get the placeholder text based on props.name
  const getPlaceholderText = () => {
    switch (props.name) {
      case "add diagram":
        return "No architecture diagrams available";
      case "add decision":
        return "No architecture decision records available";
      case "add roadmap":
        return "No roadmaps available";
      default:
        return "No items available";
    }
  };

  return (
    <div className="supporting-pattern-container">
      {/* Data Display Section */}
      <div
        className="data-display-section"
        style={{
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "20px",
          backgroundColor: "#f9f9f9",
        }}
      >
        {isLoading ? (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <SafText appearance="body-md">Loading...</SafText>
          </div>
        ) : displayItems.length > 0 ? (
          <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
            {displayItems.map((item) => (
              <li
                key={`${item.type}-${item.itemIndex}-${item.idx}`}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  marginBottom: "8px",
                  backgroundColor: "#ffffff",
                  borderRadius: "4px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  borderLeft: "3px solid #0066cc",
                }}
              >
                <div style={{ flex: 1 }}>
                  <SafAnchor
                    href={item.hyperlink}
                    target="_blank"
                    style={{ textDecoration: "none", fontWeight: "500" }}
                  >
                    {item.displayName}
                  </SafAnchor>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <SafIcon
                    icon-name="pencil"
                    appearance="solid"
                    color="#404040"
                    style={{ cursor: "pointer", marginRight: "26px" }}
                    onClick={() => handleEditClick(item)}
                  ></SafIcon>
                  <SafIcon
                    icon-name="trash-can"
                    appearance="solid"
                    color="#404040"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setDeleteIndex(item.idx);
                      setDeleteItemType(item.type);
                      setItemToEdit(supportingPatternData[item.itemIndex]);
                      setIsDeleteDialogOpen(true);
                    }}
                  ></SafIcon>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <SafText appearance="body-md">{getPlaceholderText()}</SafText>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <SafDialog
        modal={false}
        is-alert={false}
        isHeader="true"
        isFooter="true"
        no-focus-trap={false}
        dialog-title={`Do you want to delete this ${deleteItemType || "item"}?`}
        hidden={!isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <SafButton appearance="secondary" onClick={triggerDelete}>
            Yes
          </SafButton>
          <SafButton
            appearance="secondary"
            onClick={() => setIsDeleteDialogOpen(false)}
          >
            No
          </SafButton>
        </div>
      </SafDialog>

      {/* Edit Dialog */}
      <SafDialog
        modal={true}
        is-alert={false}
        isHeader="true"
        isFooter="true"
        no-focus-trap={false}
        dialog-title={`Edit ${itemToUpdate?.type || "item"}`}
        hidden={!isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
      >
        <div style={{ minWidth: "400px", padding: "16px" }}>
          <SafTextField
            label="Name"
            value={editFormData.displayName}
            onInput={(e) =>
              setEditFormData({ ...editFormData, displayName: e.target.value })
            }
            style={{ marginBottom: "16px" }}
          />
          <SafTextField
            label="link"
            value={editFormData.hyperlink}
            onInput={(e) =>
              setEditFormData({ ...editFormData, hyperlink: e.target.value })
            }
          />
        </div>
        <div
          slot="footer"
          style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
        >
          <SafButton appearance="primary" onClick={handleUpdateItem}>
            Save
          </SafButton>
          <SafButton
            appearance="secondary"
            onClick={() => setIsEditDialogOpen(false)}
          >
            Cancel
          </SafButton>
        </div>
      </SafDialog>
    </div>
  );
};

export default SupportingPatternTable;
